import {
  addPasskey,
  ConfirmDialog,
  endSessions,
  getSecurity,
  newBackupCodes,
  passkeyOptions,
  removePasskey,
  renamePasskey,
  type AccountSession,
  type Passkey,
  type Security as SecurityState,
} from '@trippath/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { browserOf, makePasskey, passkeysHere } from '../../lib/passkeys';
import {
  CodesDialog,
  NameDialog,
  OffDialog,
  PasswordDialog,
  SetupDialog,
} from './SecurityDialogs';

const locale = (language: string) => (language === 'et' ? 'et-EE' : 'en-GB');

/** `4 Oct 2026` from an instant. */
export function longDate(at: string, language: string): string {
  return new Intl.DateTimeFormat(locale(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(at));
}

/** `12.10.2026` from an instant. */
function shortDate(at: string, language: string): string {
  return new Intl.DateTimeFormat(locale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(at));
}

/** `19.10 21:14` from an instant. */
function seenAt(at: string, language: string): string {
  return new Intl.DateTimeFormat(locale(language), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(at));
}

type Open =
  | { kind: 'password' }
  | { kind: 'setup'; moving: boolean }
  | { kind: 'off' }
  | { kind: 'codes'; codes: string[] }
  | { kind: 'rename'; key: Passkey }
  | { kind: 'remove'; key: Passkey };

const KeyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="8" cy="15" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M11 12l9-9M17 6l3 3M15 8l2 2"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const PrintIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3a6 6 0 0 0-6 6v2M18 11V9a6 6 0 0 0-3-5.2M8 21c1-2 1.5-4 1.5-7a2.5 2.5 0 0 1 5 0c0 1.5-.2 3-.6 4.4M12 14c0 3-.7 5.3-2 7.5M17.5 14.5c0 2-.4 4-1 5.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * One's own account and its security (the Security mockup): the password, passkeys, two-step
 * sign-in with its backup codes, and the devices signed in.
 */
export function Security() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [state, setState] = useState<SecurityState | 'loading' | 'failed'>('loading');
  const [open, setOpen] = useState<Open | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    getSecurity().then(setState, () => {
      setState('failed');
    });
  }, []);
  useEffect(load, [load]);

  if (state === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state === 'failed') {
    return (
      <p className="page form-failed" role="alert">
        {t('trips.failed')}
      </p>
    );
  }

  const close = () => {
    setOpen(null);
  };
  const after = (job: Promise<unknown>) => {
    job.then(load, () => {
      setNote(t('security.failed'));
    });
  };
  const canPasskey = state.passkeysWork && passkeysHere();
  const addKey = () => {
    setNote(null);
    passkeyOptions()
      .then(makePasskey)
      .then((made) =>
        addPasskey({
          ...made,
          name: browserOf(navigator.userAgent) ?? t('security.passkey'),
        }),
      )
      .then(load, () => {
        setNote(t('security.passkeyFailed'));
      });
  };
  const on = state.twoStepSince !== null;
  const how = (session: AccountSession) =>
    t(
      session.signedInWith === 'code'
        ? 'security.byCode'
        : session.signedInWith === 'passkey'
          ? 'security.byPasskey'
          : 'security.byPassword',
    );

  return (
    <div className="page security-page">
      <div>
        <h1>{t('security.title')}</h1>
        <p className="quiet">{t('security.lead')}</p>
      </div>
      {note !== null && (
        <p className="form-failed" role="alert">
          {note}
        </p>
      )}
      <div className="security-layout">
        <section className="panel" aria-labelledby="security-sign-in">
          <div className="panel-head">
            <h2 id="security-sign-in">{t('security.signingIn')}</h2>
          </div>
          <div className="security-item">
            <span className="security-mark">
              <KeyIcon />
            </span>
            <div className="security-grow">
              <b>{t('security.password')}</b>
              <span className="quiet">
                {t('security.changed', { date: longDate(state.passwordChangedAt, language) })}
              </span>
            </div>
            <button
              type="button"
              className="button button-small"
              onClick={() => {
                setOpen({ kind: 'password' });
              }}
            >
              {t('security.changePassword')}
            </button>
          </div>
          <div className="security-item">
            <span className="security-mark">
              <PrintIcon />
            </span>
            <div className="security-grow">
              <b>{t('security.passkeys', { count: state.passkeys.length })}</b>
              <span className="quiet">
                {canPasskey
                  ? t('security.passkeysHint')
                  : t(
                      state.passkeysWork
                        ? 'security.passkeysNoBrowser'
                        : 'security.passkeysNeedName',
                    )}
              </span>
            </div>
            <button
              type="button"
              className="button button-small"
              disabled={!canPasskey}
              onClick={addKey}
            >
              {t('security.addPasskey')}
            </button>
          </div>
          {state.passkeys.length > 0 && (
            <div className="table-box security-keys">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t('security.passkeyName')}</th>
                    <th>{t('security.added')}</th>
                    <th>{t('security.lastUsed')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {state.passkeys.map((key) => (
                    <tr key={key.id}>
                      <td>{key.name}</td>
                      <td className="mono">{shortDate(key.createdAt, language)}</td>
                      <td className={key.lastUsedAt === null ? 'quiet' : 'mono'}>
                        {key.lastUsedAt === null
                          ? t('security.neverUsed')
                          : shortDate(key.lastUsedAt, language)}
                      </td>
                      <td className="security-actions">
                        <button
                          type="button"
                          className="link-button"
                          aria-label={`${t('security.rename')}: ${key.name}`}
                          onClick={() => {
                            setOpen({ kind: 'rename', key });
                          }}
                        >
                          {t('security.rename')}
                        </button>
                        {' · '}
                        <button
                          type="button"
                          className="link-button link-danger"
                          aria-label={`${t('security.remove')}: ${key.name}`}
                          onClick={() => {
                            setOpen({ kind: 'remove', key });
                          }}
                        >
                          {t('security.remove')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel" aria-labelledby="security-two-step">
          <div className="panel-head">
            <h2 id="security-two-step">{t('security.twoStep')}</h2>
            <span className={on ? 'security-tag security-tag-on' : 'security-tag'}>
              {t(on ? 'security.on' : 'security.off')}
            </span>
          </div>
          <div className="security-item">
            <div className="security-grow">
              <b>{t('security.app')}</b>
              <span className="quiet">
                {t('security.appHint')}
                {state.twoStepSince !== null &&
                  ` · ${t('security.appSince', { date: longDate(state.twoStepSince, language) })}`}
              </span>
            </div>
            <button
              type="button"
              className={on ? 'button button-small' : 'button button-small button-primary'}
              onClick={() => {
                setOpen({ kind: 'setup', moving: on });
              }}
            >
              {t(on ? 'security.newPhone' : 'security.turnOn')}
            </button>
          </div>
          {on && (
            <>
              <div className="security-item">
                <div className="security-grow">
                  <b>{t('security.backup', { left: state.backupLeft, total: 10 })}</b>
                  <span className="quiet">{t('security.backupHint')}</span>
                </div>
                <button
                  type="button"
                  className="button button-small"
                  onClick={() => {
                    newBackupCodes().then(
                      (codes) => {
                        setOpen({ kind: 'codes', codes });
                        load();
                      },
                      () => {
                        setNote(t('security.failed'));
                      },
                    );
                  }}
                >
                  {t('security.newCodes')}
                </button>
              </div>
              <div className="security-item">
                <div className="security-grow">
                  <span className="quiet">{t('security.offHint')}</span>
                </div>
                <button
                  type="button"
                  className="button button-small button-danger"
                  onClick={() => {
                    setOpen({ kind: 'off' });
                  }}
                >
                  {t('security.turnOff')}
                </button>
              </div>
            </>
          )}
        </section>

        <section className="panel security-devices" aria-labelledby="security-devices">
          <div className="panel-head">
            <h2 id="security-devices">{t('security.devices')}</h2>
          </div>
          <div className="table-box">
            <table className="data">
              <thead>
                <tr>
                  <th>{t('security.device')}</th>
                  <th>{t('security.lastSeen')}</th>
                  <th>{t('security.how')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {state.sessions.map((session) => {
                  const device = browserOf(session.userAgent) ?? t('security.unknownDevice');
                  return (
                    <tr key={session.id}>
                      <td>
                        <b>{device}</b>
                        {session.current && (
                          <span className="security-tag security-tag-here">
                            {t('security.thisDevice')}
                          </span>
                        )}
                      </td>
                      <td className="mono">
                        {session.current
                          ? t('security.now')
                          : seenAt(session.lastSeenAt ?? session.createdAt, language)}
                      </td>
                      <td>{how(session)}</td>
                      <td className="security-actions">
                        {!session.current && (
                          <button
                            type="button"
                            className="link-button"
                            aria-label={`${t('security.signOut')}: ${device}`}
                            onClick={() => {
                              after(endSessions(session.id));
                            }}
                          >
                            {t('security.signOut')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {state.sessions.length > 1 && (
            <div className="security-foot">
              <button
                type="button"
                className="button button-small"
                onClick={() => {
                  after(endSessions(null));
                }}
              >
                {t('security.signOutOthers')}
              </button>
            </div>
          )}
        </section>
      </div>

      {open?.kind === 'password' && (
        <PasswordDialog
          onClose={close}
          onSaved={() => {
            close();
            setNote(null);
            load();
          }}
        />
      )}
      {open?.kind === 'setup' && (
        <SetupDialog
          moving={open.moving}
          onClose={() => {
            close();
            load();
          }}
        />
      )}
      {open?.kind === 'off' && (
        <OffDialog
          onClose={close}
          onDone={() => {
            close();
            load();
          }}
        />
      )}
      {open?.kind === 'codes' && (
        <CodesDialog codes={open.codes} title={t('security.codesTitle')} onClose={close} />
      )}
      {open?.kind === 'rename' && (
        <NameDialog
          passkey={open.key}
          onClose={close}
          onSave={(name) => {
            close();
            after(renamePasskey(open.key.id, name));
          }}
        />
      )}
      {open?.kind === 'remove' && (
        <ConfirmDialog
          open
          title={t('security.removeTitle', { name: open.key.name })}
          body={t('security.removeLead')}
          confirmLabel={t('security.remove')}
          onCancel={close}
          onConfirm={() => {
            close();
            after(removePasskey(open.key.id));
          }}
        />
      )}
    </div>
  );
}
